import * as THREE from 'three'
import type { BonePoseMap, PlaybackPhase, YogaPose } from '../types'

const DEG = THREE.MathUtils.degToRad
const _euler = new THREE.Euler()
const _quat = new THREE.Quaternion()

export function captureBindPose(root: THREE.Object3D): Map<string, THREE.Quaternion> {
  const bind = new Map<string, THREE.Quaternion>()
  root.traverse((obj) => {
    if ((obj as THREE.Bone).isBone) bind.set(obj.name, obj.quaternion.clone())
  })
  return bind
}

export function boneMapToQuats(
  map: BonePoseMap,
  bind: Map<string, THREE.Quaternion>,
): Map<string, THREE.Quaternion> {
  const out = new Map<string, THREE.Quaternion>()
  for (const [name, bindQ] of bind) {
    const e = map[name]
    if (!e) { out.set(name, bindQ.clone()); continue }
    _euler.set(DEG(e[0]), DEG(e[1]), DEG(e[2]), 'XYZ')
    _quat.setFromEuler(_euler)
    out.set(name, bindQ.clone().multiply(_quat))
  }
  return out
}

export function blendQuats(
  a: Map<string, THREE.Quaternion>,
  b: Map<string, THREE.Quaternion>,
  t: number,
  bones: Map<string, THREE.Bone>,
) {
  const k = THREE.MathUtils.clamp(t, 0, 1)
  for (const [name, bone] of bones) {
    const qa = a.get(name); const qb = b.get(name)
    if (!qa || !qb) continue
    bone.quaternion.copy(qa).slerp(qb, k)
  }
}

export function applyRootTransform(
  root: THREE.Object3D,
  pose: YogaPose,
  t: number,
  basePos: THREE.Vector3,
  baseRot: THREE.Euler,
) {
  const off = pose.keyframes.rootOffset ?? [0, 0, 0]
  const rot = pose.keyframes.rootRotation ?? [0, 0, 0]
  root.position.set(basePos.x + off[0] * t, basePos.y + off[1] * t, basePos.z + off[2] * t)
  root.rotation.set(baseRot.x + DEG(rot[0]) * t, baseRot.y + DEG(rot[1]) * t, baseRot.z + DEG(rot[2]) * t)
}

export interface CycleState {
  phase: PlaybackPhase
  cycleT: number
  poseWeight: number
}

export function evaluateCycle(
  elapsed: number,
  pose: YogaPose,
  speed: number,
  holdFrozen: boolean,
): CycleState {
  const { entry, hold, release } = pose.timing
  const total = (entry + hold + release) / Math.max(speed, 0.05)
  if (holdFrozen) return { phase: 'hold', cycleT: entry / (entry + hold + release), poseWeight: 1 }
  const t = ((elapsed % (total * 1000)) / 1000) / total
  const eN = entry / (entry + hold + release)
  const hN = hold / (entry + hold + release)
  const smoothstep = (x: number) => { const u = THREE.MathUtils.clamp(x, 0, 1); return u * u * (3 - 2 * u) }
  if (t < eN) return { phase: 'entry', cycleT: t, poseWeight: smoothstep(t / eN) }
  if (t < eN + hN) {
    const u = (t - eN) / hN
    const hasHold = !!pose.keyframes.hold
    return { phase: 'hold', cycleT: t, poseWeight: hasHold ? 0.5 + 0.5 * Math.sin(u * Math.PI * 2) : 1 }
  }
  const u = (t - eN - hN) / (1 - eN - hN)
  return { phase: 'release', cycleT: t, poseWeight: 1 - smoothstep(u) }
}

export function getTargetMaps(
  pose: YogaPose,
  bind: Map<string, THREE.Quaternion>,
  phase: PlaybackPhase,
  poseWeight: number,
) {
  const poseMap = boneMapToQuats(pose.keyframes.pose, bind)
  const holdMap = boneMapToQuats(pose.keyframes.hold ?? pose.keyframes.pose, bind)
  if (phase === 'hold' && pose.keyframes.hold) return { from: poseMap, to: holdMap, t: poseWeight }
  return { from: bind, to: poseMap, t: poseWeight }
}

export function collectBones(root: THREE.Object3D): Map<string, THREE.Bone> {
  const bones = new Map<string, THREE.Bone>()
  root.traverse((obj) => { if ((obj as THREE.Bone).isBone) bones.set(obj.name, obj as THREE.Bone) })
  return bones
}
